import React, { useState, useId } from 'react';
import { parseCurrency, formatCurrency } from '../../utils/index.ts';

interface CurrencyInputProps {
    label?: React.ReactNode;
    name?: string;
    value: number | string | null | undefined;
    onChange: (value: number | undefined) => void;
    className?: string;
    placeholder?: string;
    id?: string;
    disabled?: boolean;
    /**
     * An accessible name for fields that have no visible label of their own — the funding
     * allocators, where the asset's name is a heading above a slider and this box beside it. Without
     * one a screen reader announces "text box" and nothing else, and there is no way to tell the
     * bank row's amount from the pension row's.
     */
    ariaLabel?: string;
    /** A test id on the field itself. */
    testId?: string;
}

const CurrencyInput = ({ label, name, value, onChange, className, placeholder, id, disabled, ariaLabel, testId }: CurrencyInputProps) => {
    const [error, setError] = useState('');
    /*
     * ALWAYS ASSOCIATE THE LABEL WITH THE FIELD (2026-08-27).
     *
     * `htmlFor={id}` with no `id` passed — which is almost every caller in the app — produced a
     * label pointing at nothing: clicking it did not focus the field, and a screen reader announced
     * an unlabelled text box on every dollar amount in the product. A generated id costs nothing and
     * fixes it everywhere at once; an explicit `id` still wins for callers that need a known one.
     */
    const generatedId = useId();
    const fieldId = id ?? generatedId;
    const displayValue = (value === '' || value === undefined || value === null) ? '' : formatCurrency(Number(value));
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // A '%' here means the user has confused a dollar field for a percentage one — reject the
        // keystroke (the controlled value below re-renders without it) and flag the mistake rather
        // than silently coercing e.g. "50%" into $50.
        if (e.target.value.includes('%')) {
            setError('This is a dollar amount, not a percentage — remove the "%".');
            return;
        }
        setError('');
        const raw = parseCurrency(e.target.value);
        onChange(raw === '' ? undefined : parseFloat(raw));
    };
    return (
        <div className={className}>
            {label && <label htmlFor={fieldId} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
            <input
                type="text"
                id={fieldId}
                name={name}
                aria-label={ariaLabel}
                data-testid={testId}
                value={displayValue}
                onChange={handleChange}
                placeholder={placeholder}
                disabled={disabled}
                className="w-full px-4 py-2 bg-white/80 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
        </div>
    );
};

export default CurrencyInput;
